export interface TokenPair { accessToken: string; refreshToken: string }

export interface ApiClientOptions {
  baseUrl: string;
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  setTokens: (tokens: TokenPair) => void | Promise<void>;
  clearTokens: () => void | Promise<void>;
  onSessionTerminated?: () => void;
  onRefreshed?: () => void | Promise<void>;
  onError?: (error: ApiError) => void;
}

export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
  traceId?: string;
  httpStatus: number;
}

export type ApiResponse<T> = { ok: true; data: T } | { ok: false; error: ApiError };
export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';

export interface AuthenticatedOperationContext {
  url: string;
  headers: Record<string, string>;
  signal: AbortSignal;
  isCurrent: () => boolean;
}

export function isAccessTokenFailure(status: number, body: unknown): boolean {
  if (status !== 401 || !body || typeof body !== 'object') return false;
  const error = (body as { error?: { message?: unknown; details?: { authFailure?: unknown } } }).error;
  return error?.details?.authFailure === true || (typeof error?.message === 'string'
    && ['Missing or invalid Authorization header', 'Invalid or expired access token', 'Session is no longer valid'].includes(error.message));
}

export interface ApiClient {
  authenticatedOperation<T extends { status: number }>(path: string, operation: (context: AuthenticatedOperationContext) => Promise<T>, admissionFailure: (result: T) => boolean | Promise<boolean>): Promise<T>;
  readonly identitySignal: AbortSignal;
  request<T = unknown>(method: HttpMethod, path: string, init?: RequestInit): Promise<T | ApiError>;
  get<T = unknown>(path: string, init?: RequestInit): Promise<T | ApiError>;
  post<T = unknown>(path: string, init?: RequestInit): Promise<T | ApiError>;
  patch<T = unknown>(path: string, init?: RequestInit): Promise<T | ApiError>;
  delete<T = unknown>(path: string, init?: RequestInit): Promise<T | ApiError>;
  authenticatedFetch(path: string, init?: RequestInit): Promise<Response>;
  refreshSession(expectedAccess?: string | null): Promise<boolean>;
  setSession(tokens: TokenPair): Promise<boolean>;
  resetIdentity(): number;
  clearSession(): Promise<void>;
  readonly generation: number;
  isCurrent(generation: number): boolean;
}

function staleIdentity(): Error {
  const error = new Error('Authentication identity changed');
  error.name = 'AbortError';
  return error;
}

export function bindIdentityClient(client: ApiClient): ApiClient {
  const generation = client.generation;
  const assertCurrent = () => { if (!client.isCurrent(generation)) throw staleIdentity(); };
  return {
    ...client,
    request: (...args) => { assertCurrent(); return client.request(...args); },
    get: (...args) => { assertCurrent(); return client.get(...args); },
    post: (...args) => { assertCurrent(); return client.post(...args); },
    patch: (...args) => { assertCurrent(); return client.patch(...args); },
    delete: (...args) => { assertCurrent(); return client.delete(...args); },
    authenticatedFetch: (...args) => { assertCurrent(); return client.authenticatedFetch(...args); },
    authenticatedOperation: (...args) => { assertCurrent(); return client.authenticatedOperation(...args); },
    refreshSession: (...args) => { assertCurrent(); return client.refreshSession(...args); },
    setSession: (...args) => { assertCurrent(); return client.setSession(...args); },
    resetIdentity: () => { assertCurrent(); return client.resetIdentity(); },
    clearSession: () => { assertCurrent(); return client.clearSession(); },
  };
}

export function createClient(opts: ApiClientOptions): ApiClient {
  let generation = 0;
  let identityAbort = new AbortController();
  let inflight: { generation: number; promise: Promise<boolean> } | null = null;
  let persistence: Promise<unknown> = Promise.resolve();
  const base = opts.baseUrl.replace(/\/$/, '');
  const origin = (globalThis as typeof globalThis & { location?: { origin: string } }).location?.origin ?? 'https://local.invalid';
  const baseURL = new URL(base, origin);
  const current = (g: number) => g === generation;
  const assertCurrent = (g: number) => { if (!current(g)) throw staleIdentity(); };
  const persist = <T>(fn: () => Promise<T> | T): Promise<T> => {
    const next = persistence.then(fn);
    persistence = next.catch(() => {});
    return next;
  };
  const resetIdentity = () => {
    generation++;
    identityAbort.abort();
    identityAbort = new AbortController();
    inflight = null;
    return generation;
  };
  const clearSession = async () => {
    const g = resetIdentity();
    opts.onSessionTerminated?.();
    await persist(async () => { if (current(g)) await opts.clearTokens(); });
  };
  const setSession = async (tokens: TokenPair) => {
    const g = generation;
    if (!tokens.accessToken || !tokens.refreshToken) throw new Error('Invalid authentication response');
    await persist(async () => { assertCurrent(g); await opts.setTokens(tokens); });
    return current(g);
  };
  const refreshSession = (expectedAccess?: string | null): Promise<boolean> => {
    if (expectedAccess !== undefined && opts.getAccessToken() && opts.getAccessToken() !== expectedAccess) return Promise.resolve(true);
    if (inflight?.generation === generation) return inflight.promise;
    const g = generation;
    const refreshToken = opts.getRefreshToken();
    if (!refreshToken) return Promise.resolve(false);
    const promise = (async () => {
      const res = await fetch(`${base}/auth/refresh`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }), signal: identityAbort.signal, redirect: 'error',
      });
      assertCurrent(g);
      if (res.status === 401 || res.status === 403) { await clearSession(); return false; }
      if (!res.ok) throw new Error(`Session refresh temporarily failed (${res.status})`);
      const body = await res.json() as { ok?: boolean; data?: Partial<TokenPair> };
      assertCurrent(g);
      if (!body.ok || typeof body.data?.accessToken !== 'string' || !body.data.accessToken
        || typeof body.data.refreshToken !== 'string' || !body.data.refreshToken) throw new Error('Invalid session refresh response');
      if (!(await setSession(body.data as TokenPair))) throw staleIdentity();
      await opts.onRefreshed?.();
      assertCurrent(g);
      return true;
    })().finally(() => { if (inflight?.promise === promise) inflight = null; });
    inflight = { generation: g, promise };
    return promise;
  };

  const authenticatedFetch = async (path: string, init: RequestInit = {}): Promise<Response> => {
    const url = new URL(path.startsWith('/') || /^https?:\/\//.test(path) ? path : `${base}/${path}`, baseURL.origin);
    const trusted = url.origin === baseURL.origin
      && (url.pathname === baseURL.pathname || url.pathname.startsWith(`${baseURL.pathname.replace(/\/$/, '')}/`));
    const relative = url.pathname.slice(baseURL.pathname.replace(/\/$/, '').length);
    const publicRoute = /^\/(public(?:\/|$)|proposals\/(?:public|accept|negotiate)(?:\/|$)|auth\/(?:login(?:\/|$)|refresh(?:\/|$)|forgot-password|reset-password|invite(?:\/|$)|mfa\/recovery))/.test(relative);
    const headers = new Headers(init.headers);
    headers.delete('Authorization');
    if (!trusted || publicRoute) return fetch(url.toString(), { ...init, headers });
    const g = generation;
    const requestToken = opts.getAccessToken();
    const signal = identityAbort.signal;
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal.addEventListener('abort', abort, { once: true });
    init.signal?.addEventListener('abort', abort, { once: true });
    if (signal.aborted || init.signal?.aborted) controller.abort();
    const perform = async () => {
      assertCurrent(g);
      if (controller.signal.aborted) throw staleIdentity();
      const token = opts.getAccessToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);
      else headers.delete('Authorization');
      const response = await fetch(url.toString(), { ...init, headers, signal: controller.signal, redirect: 'error' });
      assertCurrent(g);
      return response;
    };
    const release = () => {
      signal.removeEventListener('abort', abort);
      init.signal?.removeEventListener('abort', abort);
    };
    try {
      let response = await perform();
      const factorRoute = relative.startsWith('/auth/') && relative !== '/auth/session';
      const sessionRejected = async (res: Response) => {
        if (res.status !== 401) return false;
        try {
          const body = await res.clone().json() as { error?: { message?: string; details?: { authFailure?: boolean } } };
          if (body.error?.details?.authFailure === true) return true;
          if (body.error?.message) return ['Missing or invalid Authorization header', 'Invalid or expired access token', 'Session is no longer valid'].includes(body.error.message);
        } catch {}
        return !factorRoute;
      };
      const replayable = !(typeof ReadableStream !== 'undefined' && init.body instanceof ReadableStream);
      if (await sessionRejected(response)) {
        assertCurrent(g);
        if (opts.getRefreshToken()) {
          if (await refreshSession(requestToken)) {
            assertCurrent(g);
            if (!replayable) throw new Error('Request body cannot be replayed after authentication refresh');
            await response.body?.cancel();
            response = await perform();
          } else throw staleIdentity();
        }
        if (await sessionRejected(response)) { assertCurrent(g); await clearSession(); throw staleIdentity(); }
      }
      for (const method of ['json', 'text', 'blob', 'arrayBuffer', 'formData'] as const) {
        const read = response[method].bind(response);
        Object.defineProperty(response, method, { value: async () => {
          try { const body = await read(); assertCurrent(g); return body; }
          finally { release(); }
        } });
      }
      if (response.body) {
        const getReader = response.body.getReader.bind(response.body);
        Object.defineProperty(response.body, 'getReader', { value: (...args: Parameters<typeof getReader>) => {
          const reader = getReader(...args);
          const read = reader.read.bind(reader);
          Object.defineProperty(reader, 'read', { value: async (...readArgs: Parameters<typeof read>) => {
            try {
              assertCurrent(g);
              const chunk = await Reflect.apply(read, reader, readArgs);
              assertCurrent(g);
              if (chunk.done) release();
              return chunk;
            } catch (error) { void reader.cancel().catch(() => {}); release(); throw error; }
          } });
          return reader;
        } });
      } else release();
      return response;
    } catch (error) { release(); throw error; }
  };

  const call = async <T>(method: HttpMethod, path: string, init: RequestInit = {}): Promise<T | ApiError> => {
    const headers = new Headers(init.headers);
    if (typeof init.body === 'string' && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    const response = await authenticatedFetch(`${base}${path}`, { ...init, method, headers });
    if (response.status === 204) return null as T;
    if (!response.ok) {
      let body: { error?: ApiError } | null = null;
      try { body = await response.json() as { error?: ApiError }; } catch (error) { if ((error as Error).name === 'AbortError') throw error; }
      const error: ApiError = { code: body?.error?.code ?? 'INTERNAL_ERROR', message: body?.error?.message ?? response.statusText,
        details: body?.error?.details, traceId: body?.error?.traceId, httpStatus: response.status };
      opts.onError?.(error);
      return { ok: false, error } as T | ApiError;
    }
    const body = await response.json() as { ok: boolean; data?: T; error?: ApiError };
    if (!body.ok || body.data === undefined) return { ok: false, error: body.error ?? { code: 'INTERNAL_ERROR', message: response.statusText, httpStatus: response.status } } as T | ApiError;
    return body.data;
  };
  const authenticatedOperation: ApiClient['authenticatedOperation'] = async (path, operation, admissionFailure) => {
    if (!path.startsWith('/') || path.startsWith('//') || path.includes('://')) throw new Error('Native operation requires a private API path');
    const url = new URL(`${base}${path}`, origin);
    if (url.origin !== baseURL.origin || !url.pathname.startsWith(`${baseURL.pathname.replace(/\/$/, '')}/`)
      || /^\/(public|auth|proposals\/(public|accept|negotiate))(\/|$)/.test(path)) throw new Error('Native operation requires a private API path');
    const g = generation;
    const requestToken = opts.getAccessToken();
    const signal = identityAbort.signal;
    const run = async () => {
      assertCurrent(g);
      if (signal.aborted || !opts.getAccessToken()) throw staleIdentity();
      const result = await operation({ url: url.toString(), headers: { Authorization: `Bearer ${opts.getAccessToken()}` }, signal,
        isCurrent: () => current(g) && !signal.aborted });
      assertCurrent(g);
      return result;
    };
    let result = await run();
    if (result.status === 401 && await admissionFailure(result)) {
      assertCurrent(g);
      if (!opts.getRefreshToken() || !(await refreshSession(requestToken))) {
        if (current(g)) await clearSession();
        throw staleIdentity();
      }
      assertCurrent(g);
      result = await run();
      if (result.status === 401 && await admissionFailure(result)) {
        assertCurrent(g); await clearSession(); throw staleIdentity();
      }
    }
    assertCurrent(g);
    return result;
  };
  return {
    request: call, get: (p, i) => call('GET', p, i), post: (p, i) => call('POST', p, i),
    patch: (p, i) => call('PATCH', p, i), delete: (p, i) => call('DELETE', p, i),
    authenticatedFetch, authenticatedOperation, refreshSession, setSession, resetIdentity, clearSession,
    get identitySignal() { return identityAbort.signal; },
    get generation() { return generation; }, isCurrent: current,
  };
}
